import { useMemo, useState, type CSSProperties } from "react";
import type { PageBlock, PageImageBlock, PageModel, PageTextBlock } from "../../../shared/pageModel";
import { Button } from "../ui/button";
import { EmptyState } from "../ui/empty-state";

type Props = {
  pages: PageModel[];
  /** CSS pixels per PDF point (1pt = 1/72"). */
  scale?: number;
};

/** Map pdfkit registered names (Body-EBGaramond-Regular) onto CSS families. */
function cssFontForBlock(block: PageTextBlock): {
  fontFamily: string;
  fontStyle: "normal" | "italic";
  fontWeight: number;
} {
  const name = block.font || "";
  const lower = name.toLowerCase();
  const italic = Boolean(block.italic) || lower.includes("italic");
  const bold = Boolean(block.bold) || lower.includes("bold");
  const fontWeight = bold ? 700 : 400;
  const fontStyle = italic ? "italic" : "normal";

  if (lower.includes("ebgaramond") || lower.includes("garamond")) {
    return { fontFamily: '"EB Garamond", Georgia, serif', fontStyle, fontWeight };
  }
  if (lower.includes("crimson")) {
    return { fontFamily: '"Crimson Text", Georgia, serif', fontStyle, fontWeight };
  }
  if (lower.includes("baskerville") || lower.includes("librebaskerville")) {
    return { fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle, fontWeight };
  }
  if (lower.includes("liberation")) {
    return { fontFamily: '"Liberation Serif", Georgia, serif', fontStyle, fontWeight };
  }
  if (lower.includes("playfair")) {
    return { fontFamily: '"Playfair Display", Georgia, serif', fontStyle, fontWeight };
  }
  if (lower.includes("cinzel")) {
    return { fontFamily: '"Cinzel", Georgia, serif', fontStyle, fontWeight };
  }
  return { fontFamily: 'Georgia, "Times New Roman", serif', fontStyle, fontWeight };
}

/**
 * Preview alignment: never use CSS `justify` on single pre-wrapped lines.
 * When the browser font is slightly wider than pdfkit's measure, justify
 * compresses word-spacing (even negative) and glyphs stack into a smear.
 */
function previewAlign(align: PageTextBlock["align"]): "left" | "center" | "right" {
  if (align === "center") return "center";
  if (align === "right") return "right";
  return "left";
}

/** Shared text styles for absolutely positioned preview lines. */
function textBlockBaseStyle(opts: {
  top: number;
  width: number;
  fontSizePx: number;
  lineHeightPx: number;
  height: number;
  fontFamily: string;
  opacity?: number;
  overflow?: "hidden" | "visible";
}): CSSProperties {
  return {
    position: "absolute",
    top: opts.top,
    width: opts.width,
    fontSize: opts.fontSizePx,
    lineHeight: `${opts.lineHeightPx}px`,
    height: opts.height,
    whiteSpace: "nowrap",
    overflow: opts.overflow ?? "hidden",
    opacity: opts.opacity ?? 1,
    boxSizing: "border-box",
    fontFamily: opts.fontFamily,
  };
}

function TextBlockView({
  block,
  scale,
  pageWidth,
}: {
  block: PageTextBlock;
  scale: number;
  pageWidth: number;
}) {
  const indent = block.indent ?? 0;
  const align = previewAlign(block.align);
  const fontSizePx = block.fontSize * scale;
  const isDropCap = block.role === "drop-cap";
  // Drop caps are a single glyph sized to N body lines. Use a full em line-box
  // (not 0.9) so the letter isn't clipped; overflow stays visible for serifs.
  const lineHeightPx = isDropCap ? fontSizePx : fontSizePx * 1.15;
  const { fontFamily } = cssFontForBlock(block);
  const top = block.y * scale;
  const boxWidthPt = Math.max(1, block.width);
  const height = isDropCap ? fontSizePx : lineHeightPx;

  /**
   * Placement:
   * - drop-cap: honor the paginator slot so body lines beside it stay clear.
   * - right: pin the box with CSS `right` so headers/TOC numbers sit on the
   *   content margin (never stretch to the page trim — that caused bleed).
   * - center: honor the paginator box.
   * - left: allow slack into the trailing margin for wider CSS faces, but keep
   *   1.5pt clear of the trim so glyphs don't paint past the page.
   */
  let positionStyle: CSSProperties;
  if (isDropCap) {
    positionStyle = {
      left: block.x * scale,
      width: boxWidthPt * scale,
      textAlign: "left",
    };
  } else if (align === "right") {
    const rightInsetPt = Math.max(0, pageWidth - block.x - boxWidthPt);
    positionStyle = {
      right: rightInsetPt * scale,
      left: "auto",
      width: boxWidthPt * scale,
      textAlign: "right",
    };
  } else if (align === "center") {
    positionStyle = {
      left: block.x * scale,
      width: boxWidthPt * scale,
      textAlign: "center",
    };
  } else {
    const leftPt = block.x + indent;
    const maxW = Math.max(1, pageWidth - leftPt - 1.5);
    positionStyle = {
      left: leftPt * scale,
      width: maxW * scale,
      textAlign: "left",
    };
  }

  const base = textBlockBaseStyle({
    top,
    width: Number(positionStyle.width),
    fontSizePx,
    lineHeightPx,
    height,
    fontFamily,
    opacity: block.opacity,
    overflow: isDropCap ? "visible" : "hidden",
  });

  if (block.runs && block.runs.length > 0) {
    return (
      <div style={{ ...base, ...positionStyle }}>
        {block.runs.map((run, i) => (
          <span
            key={i}
            style={{
              fontWeight: run.bold ? 700 : 400,
              fontStyle: run.italic ? "italic" : "normal",
              textDecoration: run.underline ? "underline" : "none",
              fontFamily,
            }}
          >
            {run.text}
          </span>
        ))}
      </div>
    );
  }

  const { fontStyle, fontWeight } = cssFontForBlock(block);
  return (
    <div
      style={{
        ...base,
        ...positionStyle,
        fontStyle,
        fontWeight: isDropCap ? 700 : fontWeight,
        textDecoration: block.underline ? "underline" : "none",
      }}
    >
      {block.text}
    </div>
  );
}

function ImageBlockView({ block, scale }: { block: PageImageBlock; scale: number }) {
  return (
    <img
      alt=""
      src={block.src}
      style={{
        position: "absolute",
        left: block.x * scale,
        top: block.y * scale,
        width: block.width * scale,
        height: block.height * scale,
        objectFit: "contain",
        opacity: block.opacity ?? 1,
        pointerEvents: "none",
      }}
    />
  );
}

function PageSurface({ page, scale }: { page: PageModel; scale: number }) {
  // Integer CSS px avoids subpixel clip gaps where antialiased glyphs bleed past the trim.
  const w = Math.round(page.width * scale);
  const h = Math.round(page.height * scale);
  return (
    <div
      className="relative shrink-0 overflow-hidden bg-white text-[#111] shadow-sm [clip-path:inset(0)] contain-[paint]"
      style={{
        width: w,
        height: h,
      }}
      data-page={page.pageNumber}
      data-side={page.side}
    >
      {page.blocks.map((block: PageBlock, i) => {
        if (block.type === "rule") {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: block.x1 * scale,
                top: block.y1 * scale,
                width: Math.max(1, (block.x2 - block.x1) * scale),
                height: Math.max(1, (block.lineWidth ?? 0.5) * scale),
                background: `rgba(0,0,0,${block.opacity ?? 0.4})`,
              }}
            />
          );
        }
        if (block.type === "image") {
          return <ImageBlockView key={i} block={block} scale={scale} />;
        }
        return <TextBlockView key={i} block={block} scale={scale} pageWidth={page.width} />;
      })}
    </div>
  );
}

/**
 * Two-up verso|recto spread preview driven by PageModel[] from the shared paginator.
 */
export function WysiwygSpreadPreview({ pages, scale = 0.85 }: Props) {
  const [spreadIndex, setSpreadIndex] = useState(0);

  const spreads = useMemo(() => {
    const out: Array<{ verso: PageModel | null; recto: PageModel | null }> = [];
    if (!pages.length) return out;

    // Book opens with page 1 (recto) alone, then verso|recto pairs.
    let i = 0;
    if (pages[0]?.side === "recto") {
      out.push({ verso: null, recto: pages[0] });
      i = 1;
    }
    while (i < pages.length) {
      const a = pages[i];
      const b = pages[i + 1] ?? null;
      if (a.side === "verso") {
        out.push({ verso: a, recto: b?.side === "recto" ? b : null });
        i += b?.side === "recto" ? 2 : 1;
      } else {
        out.push({ verso: null, recto: a });
        i += 1;
      }
    }
    return out;
  }, [pages]);

  const spread = spreads[Math.min(spreadIndex, Math.max(0, spreads.length - 1))] ?? null;
  if (!pages.length) {
    return (
      <EmptyState
        compact
        title="No pages to preview"
        description="Add manuscript content to generate a print preview."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={spreadIndex <= 0}
          onClick={() => setSpreadIndex((n) => Math.max(0, n - 1))}
        >
          ← Prev
        </Button>
        <span className="text-xs text-muted-foreground">
          Spread {spreadIndex + 1} / {spreads.length} · {pages.length} pages
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={spreadIndex >= spreads.length - 1}
          onClick={() => setSpreadIndex((n) => Math.min(spreads.length - 1, n + 1))}
        >
          Next →
        </Button>
      </div>
      <div
        className="flex items-start justify-center gap-3 overflow-auto rounded-lg bg-muted/35 p-4"
      >
        {spread?.verso ? (
          <PageSurface page={spread.verso} scale={scale} />
        ) : (
          <div
            className="shrink-0 bg-transparent"
            style={{
              width: Math.round((pages[0]?.width ?? 432) * scale),
              height: Math.round((pages[0]?.height ?? 648) * scale),
            }}
          />
        )}
        {spread?.recto ? <PageSurface page={spread.recto} scale={scale} /> : null}
      </div>
    </div>
  );
}
